import { isCharacterId } from "./content";
import { clampLevelNumber } from "../levels";
import {
  validateEquippedAccessoryId,
  type AccessoryId,
} from "../wardrobe";
import {
  copyMathProgress,
  createDefaultMathProgress,
  DEFAULT_MATH_PROGRESS,
  DEFAULT_SETTINGS,
  EMPTY_COLLECTIBLES,
  normalizeSettings,
} from "./state";
import { SAVED_MATH_SKILL_IDS } from "./types";
import type {
  CharacterId,
  CollectibleInventory,
  GameSession,
  GameSettings,
  MathDifficulty,
  MathProgress,
  SavedMathSkillId,
  SavedMathSkillMastery,
} from "./types";

export const SAVE_VERSION = 3 as const;
export const DEFAULT_SAVE_KEY = "magic-adventures.save";

export interface CharacterSaveProgress {
  readonly highestUnlockedLevel: number;
  readonly completedLevelIds: readonly string[];
  readonly bestCollectibles: CollectibleInventory;
  readonly equippedAccessoryId: AccessoryId | null;
}

export interface SaveGameV3 {
  readonly version: typeof SAVE_VERSION;
  readonly selectedCharacterId: CharacterId | null;
  readonly characterProgress: Readonly<
    Record<CharacterId, CharacterSaveProgress>
  >;
  readonly settings: GameSettings;
  readonly mathProgress: MathProgress;
  readonly updatedAt: string;
}

export interface StorageAdapter {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export type SaveLoadStatus = "empty" | "loaded" | "migrated" | "recovered";

export interface SaveLoadResult {
  readonly save: SaveGameV3;
  readonly status: SaveLoadStatus;
}

export interface SaveRepository {
  load(): SaveLoadResult;
  save(save: SaveGameV3): boolean;
  clear(): boolean;
}

export interface SaveRepositoryOptions {
  readonly key?: string;
  readonly now?: () => string;
}

function emptyCharacterProgress(): CharacterSaveProgress {
  return {
    highestUnlockedLevel: 1,
    completedLevelIds: [],
    bestCollectibles: { ...EMPTY_COLLECTIBLES },
    equippedAccessoryId: null,
  };
}

export function createDefaultSave(updatedAt: string): SaveGameV3 {
  return {
    version: SAVE_VERSION,
    selectedCharacterId: null,
    characterProgress: {
      dragon: emptyCharacterProgress(),
      unicorn: emptyCharacterProgress(),
    },
    settings: { ...DEFAULT_SETTINGS },
    mathProgress: copyMathProgress(DEFAULT_MATH_PROGRESS),
    updatedAt,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function finiteInteger(value: unknown, minimum = 0): number | null {
  if (
    typeof value !== "number" ||
    !Number.isFinite(value) ||
    !Number.isInteger(value) ||
    value < minimum
  ) {
    return null;
  }
  return value;
}

function decodeInventory(value: unknown): CollectibleInventory | null {
  if (!isRecord(value)) return null;
  const coins = finiteInteger(value.coins);
  const gems = finiteInteger(value.gems);
  const wisdomStars = finiteInteger(value.wisdomStars);
  const hearts = finiteInteger(value.hearts);
  if (
    coins === null ||
    gems === null ||
    wisdomStars === null ||
    hearts === null
  ) {
    return null;
  }
  return { coins, gems, wisdomStars, hearts };
}

function decodeCharacterProgress(
  value: unknown,
  characterId: CharacterId,
): CharacterSaveProgress | null {
  if (!isRecord(value)) return null;
  const highestUnlockedLevel = finiteInteger(value.highestUnlockedLevel, 1);
  const bestCollectibles = decodeInventory(value.bestCollectibles);
  if (
    highestUnlockedLevel === null ||
    bestCollectibles === null ||
    !Array.isArray(value.completedLevelIds) ||
    !value.completedLevelIds.every((id) => typeof id === "string")
  ) {
    return null;
  }
  const completedLevelIds = [...new Set(value.completedLevelIds)];
  return {
    highestUnlockedLevel: clampLevelNumber(highestUnlockedLevel),
    completedLevelIds,
    bestCollectibles,
    equippedAccessoryId: validateEquippedAccessoryId(
      characterId,
      value.equippedAccessoryId,
      completedLevelIds,
    ),
  };
}

function decodeSettings(value: unknown): GameSettings | null {
  if (!isRecord(value)) return null;
  if (
    value.locale !== "he-IL" ||
    typeof value.musicVolume !== "number" ||
    typeof value.effectsVolume !== "number" ||
    typeof value.muted !== "boolean" ||
    typeof value.reducedMotion !== "boolean" ||
    typeof value.highContrast !== "boolean" ||
    typeof value.touchControls !== "boolean"
  ) {
    return null;
  }
  return normalizeSettings({
    locale: "he-IL",
    musicVolume: value.musicVolume,
    effectsVolume: value.effectsVolume,
    muted: value.muted,
    reducedMotion: value.reducedMotion,
    highContrast: value.highContrast,
    touchControls: value.touchControls,
  });
}

function isMathDifficulty(value: unknown): value is MathDifficulty {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= 1 &&
    value <= 6
  );
}

function decodeSkillMastery(value: unknown): SavedMathSkillMastery | null {
  if (!isRecord(value)) return null;
  const consecutiveMisses = finiteInteger(value.consecutiveMisses);
  const totalAttempts = finiteInteger(value.totalAttempts);
  const totalCorrect = finiteInteger(value.totalCorrect);
  if (
    !isMathDifficulty(value.currentTier) ||
    !Array.isArray(value.recentResults) ||
    value.recentResults.length > 5 ||
    !value.recentResults.every((result) => typeof result === "boolean") ||
    consecutiveMisses === null ||
    typeof value.supportActive !== "boolean" ||
    totalAttempts === null ||
    totalCorrect === null ||
    totalCorrect > totalAttempts
  ) {
    return null;
  }
  return {
    currentTier: value.currentTier,
    recentResults: [...value.recentResults],
    consecutiveMisses,
    supportActive: value.supportActive,
    totalAttempts,
    totalCorrect,
  };
}

function decodeMasteryBySkill(
  value: unknown,
  fallbackDifficulty: MathDifficulty,
): MathProgress["masteryBySkill"] | null {
  const defaults = createDefaultMathProgress(fallbackDifficulty).masteryBySkill;
  if (value === undefined) return defaults;
  if (!isRecord(value)) return null;
  const entries: Array<[SavedMathSkillId, SavedMathSkillMastery]> = [];
  for (const skill of SAVED_MATH_SKILL_IDS) {
    const stored = value[skill];
    if (stored === undefined) {
      // A skill added in a later release is absent from older saves; seed it
      // at the save's difficulty instead of rejecting the whole save.
      entries.push([skill, defaults[skill]]);
      continue;
    }
    const mastery = decodeSkillMastery(stored);
    if (!mastery) return null;
    entries.push([skill, mastery]);
  }
  return Object.fromEntries(entries) as Record<SavedMathSkillId, SavedMathSkillMastery>;
}

function decodeMathProgress(value: unknown): MathProgress | null {
  if (!isRecord(value)) return null;
  const correctAnswers = finiteInteger(value.correctAnswers);
  const incorrectAnswers = finiteInteger(value.incorrectAnswers);
  if (
    !isMathDifficulty(value.difficulty) ||
    correctAnswers === null ||
    incorrectAnswers === null ||
    !Array.isArray(value.recentResults) ||
    value.recentResults.length > 5 ||
    !value.recentResults.every((result) => typeof result === "boolean")
  ) {
    return null;
  }
  const masteryBySkill = decodeMasteryBySkill(value.masteryBySkill, value.difficulty);
  if (!masteryBySkill) return null;
  return {
    difficulty: value.difficulty,
    correctAnswers,
    incorrectAnswers,
    recentResults: [...value.recentResults],
    masteryBySkill,
  };
}

function decodeV3(value: unknown): SaveGameV3 | null {
  if (
    !isRecord(value) ||
    value.version !== SAVE_VERSION ||
    !(value.selectedCharacterId === null ||
      isCharacterId(value.selectedCharacterId)) ||
    !isRecord(value.characterProgress) ||
    typeof value.updatedAt !== "string"
  ) {
    return null;
  }
  const dragon = decodeCharacterProgress(value.characterProgress.dragon, "dragon");
  const unicorn = decodeCharacterProgress(value.characterProgress.unicorn, "unicorn");
  const settings = decodeSettings(value.settings);
  const mathProgress = decodeMathProgress(value.mathProgress);
  if (!dragon || !unicorn || !settings || !mathProgress) return null;
  return {
    version: SAVE_VERSION,
    selectedCharacterId: value.selectedCharacterId,
    characterProgress: { dragon, unicorn },
    settings,
    mathProgress,
    updatedAt: value.updatedAt,
  };
}

function migrateV2(value: unknown, updatedAt: string): SaveGameV3 | null {
  if (
    !isRecord(value) ||
    value.version !== 2 ||
    !(value.selectedCharacterId === null || isCharacterId(value.selectedCharacterId)) ||
    !isRecord(value.characterProgress)
  ) {
    return null;
  }
  const dragon = decodeCharacterProgress(value.characterProgress.dragon, "dragon");
  const unicorn = decodeCharacterProgress(value.characterProgress.unicorn, "unicorn");
  const settings = decodeSettings(value.settings);
  const mathProgress = decodeMathProgress(value.mathProgress);
  if (!dragon || !unicorn || !settings || !mathProgress) return null;
  return {
    version: SAVE_VERSION,
    selectedCharacterId: value.selectedCharacterId,
    characterProgress: { dragon, unicorn },
    settings,
    mathProgress,
    updatedAt,
  };
}

function valueOrZero(value: unknown): number {
  return finiteInteger(value) ?? 0;
}

function migrateV1(value: unknown, updatedAt: string): SaveGameV3 | null {
  if (!isRecord(value) || value.version !== 1) return null;
  const result = createDefaultSave(updatedAt);
  const selectedCandidate =
    value.selectedCharacterId ?? value.selectedCharacter ?? value.character;
  const selectedCharacterId = isCharacterId(selectedCandidate)
    ? selectedCandidate
    : null;
  const oldLevel = finiteInteger(value.highestUnlockedLevel, 1) ?? 1;
  const oldInventory: CollectibleInventory = {
    coins: valueOrZero(value.coins),
    gems: valueOrZero(value.gems),
    wisdomStars: valueOrZero(value.wisdomStars),
    hearts: valueOrZero(value.hearts),
  };
  const difficulty = isMathDifficulty(value.mathDifficulty)
    ? value.mathDifficulty
    : result.mathProgress.difficulty;
  const settings = normalizeSettings({
    ...result.settings,
    muted: value.soundEnabled === false,
    reducedMotion:
      typeof value.reducedMotion === "boolean"
        ? value.reducedMotion
        : result.settings.reducedMotion,
  });
  const migratedCharacterProgress = {
    dragon: emptyCharacterProgress(),
    unicorn: emptyCharacterProgress(),
  };
  if (selectedCharacterId !== null) {
    migratedCharacterProgress[selectedCharacterId] = {
      highestUnlockedLevel: oldLevel,
      completedLevelIds: [],
      bestCollectibles: oldInventory,
      equippedAccessoryId: null,
    };
  }
  return {
    ...result,
    selectedCharacterId,
    characterProgress: migratedCharacterProgress,
    settings,
    mathProgress: createDefaultMathProgress(difficulty),
  };
}

function stampSave(save: SaveGameV3, updatedAt: string): SaveGameV3 {
  return { ...save, version: SAVE_VERSION, updatedAt };
}

export function createSaveRepository(
  storage: StorageAdapter,
  options: SaveRepositoryOptions = {},
): SaveRepository {
  const key = options.key ?? DEFAULT_SAVE_KEY;
  const now = options.now ?? (() => new Date().toISOString());

  function write(save: SaveGameV3): boolean {
    try {
      storage.setItem(key, JSON.stringify(save));
      return true;
    } catch {
      return false;
    }
  }

  return {
    load(): SaveLoadResult {
      const fallback = (): SaveLoadResult => ({
        save: createDefaultSave(now()),
        status: "recovered",
      });
      let raw: string | null;
      try {
        raw = storage.getItem(key);
      } catch {
        return fallback();
      }
      if (raw === null) {
        return { save: createDefaultSave(now()), status: "empty" };
      }
      let parsed: unknown;
      try {
        parsed = JSON.parse(raw) as unknown;
      } catch {
        return fallback();
      }
      const current = decodeV3(parsed);
      if (current) return { save: current, status: "loaded" };
      const migratedV2 = migrateV2(parsed, now());
      if (migratedV2) {
        write(migratedV2);
        return { save: migratedV2, status: "migrated" };
      }
      const migrated = migrateV1(parsed, now());
      if (migrated) {
        write(migrated);
        return { save: migrated, status: "migrated" };
      }
      return fallback();
    },

    save(save: SaveGameV3): boolean {
      const normalized = decodeV3(stampSave(save, now()));
      return normalized === null ? false : write(normalized);
    },

    clear(): boolean {
      try {
        storage.removeItem(key);
        return true;
      } catch {
        return false;
      }
    },
  };
}

export function applySessionToSave(
  save: SaveGameV3,
  session: GameSession,
): SaveGameV3 {
  const previous = save.characterProgress[session.characterId];
  const completedLevelIds = previous.completedLevelIds.includes(session.levelId)
    ? previous.completedLevelIds
    : [...previous.completedLevelIds, session.levelId];
  return {
    ...save,
    selectedCharacterId: session.characterId,
    characterProgress: {
      ...save.characterProgress,
      [session.characterId]: {
        highestUnlockedLevel: Math.max(
          previous.highestUnlockedLevel,
          clampLevelNumber(session.levelNumber + 1),
        ),
        completedLevelIds,
        equippedAccessoryId: previous.equippedAccessoryId,
        bestCollectibles: {
          coins: Math.max(
            previous.bestCollectibles.coins,
            session.collectibles.coins,
          ),
          gems: Math.max(
            previous.bestCollectibles.gems,
            session.collectibles.gems,
          ),
          wisdomStars: Math.max(
            previous.bestCollectibles.wisdomStars,
            session.collectibles.wisdomStars,
          ),
          hearts: Math.max(
            previous.bestCollectibles.hearts,
            session.collectibles.hearts,
          ),
        },
      },
    },
    mathProgress: session.mathProgress,
  };
}

export class MemoryStorageAdapter implements StorageAdapter {
  private readonly values = new Map<string, string>();

  getItem(key: string): string | null {
    return this.values.get(key) ?? null;
  }

  setItem(key: string, value: string): void {
    this.values.set(key, value);
  }

  removeItem(key: string): void {
    this.values.delete(key);
  }
}
